// The partner crabs and the night verifier. A bot tries candidate shots on the
// real resolveTurn and keeps the one with the best outcome, so whatever it
// plans replays exactly in the game.
import { resolveTurn, darkForecast, litCells, bossWater, openCells, progressScore } from './pool.js';
import { cellAt } from './grid.js';

// 32 directions as integer vectors (literal so every engine plans the same).
export const DIRS = [[1000,0],[981,195],[924,383],[831,556],[707,707],[556,831],[383,924],[195,981],[0,1000],[-195,981],[-383,924],[-556,831],[-707,707],[-831,556],[-924,383],[-981,195],[-1000,0],[-981,-195],[-924,-383],[-831,-556],[-707,-707],[-556,-831],[-383,-924],[-195,-981],[0,-1000],[195,-981],[383,-924],[556,-831],[707,-707],[831,-556],[924,-383],[981,-195]];

// How good a turn's outcome is for the team.
export function scoreOutcome(before, res) {
  const p = res.pool;
  let s = 0;
  for (const e of res.events) {
    if (e.type === 'saved') s += 1000;
    if (e.type === 'taken' || e.type === 'faded') s -= 1400;
    if (e.type === 'calm') s += 6000;
  }
  s += (litCells(p.grid) - litCells(before.grid)) * 3;
  s += (progressScore(p) - progressScore(before)) * 10;
  // In the deep pool, shrinking the old one's water is progress in itself.
  if (p.boss && !p.boss.calm) s -= Math.round((bossWater(p) * 4000) / openCells(p.grid));
  // Little ones the dark will reach next turn are in danger.
  const danger = new Set(darkForecast(p));
  for (const l of p.littles) {
    if (l.state !== 'lost' || l.kind === 'float') continue;
    const i = cellAt(p.grid, l.x, l.y);
    if (danger.has(i)) s -= 450;
  }
  return s;
}

// Candidate shots for one crab: `dirs` directions x `powers`, plus holding still.
export function candidates(dirs = 24, powers = [1000, 620]) {
  const step = DIRS.length / dirs;
  const out = [null];
  for (let k = 0; k < dirs; k++) {
    const [dx, dy] = DIRS[Math.round(k * step) % DIRS.length];
    for (const power of powers) out.push({ dx, dy, power });
  }
  return out;
}

// Choose shots for `crabIds`, one after another, given the inputs already
// fixed (humans first, then earlier bots). Returns the full input list.
export function planTurn(pool, fixed, crabIds, { dirs = 24, powers = [1000, 620], noise = null } = {}) {
  const inputs = [...fixed];
  const cands = candidates(dirs, powers);
  for (const id of crabIds) {
    let best = null, bestScore = -Infinity;
    for (const c of cands) {
      const trial = c ? [...inputs, { crab: id, ...c }] : inputs;
      const res = resolveTurn(pool, trial, { record: false });
      const sc = scoreOutcome(pool, res) - (c ? 0 : 5);
      if (sc > bestScore) { bestScore = sc; best = c; }
    }
    if (best) inputs.push({ crab: id, ...(noise ? noise(best) : best) });
  }
  return inputs;
}

// Partner crabs in the game: a coarser search and a slightly unsteady claw,
// so they help without playing the night for you. `seed` keeps it deterministic.
export const PARTNER = { dirs: 12, powers: [1000, 650], deg: 7, pct: 12 };

// A human stand-in: plans like the bot on a coarser set, then misses a bit.
export function humanNoise(r, degrees = 9, powerPct = 18) {
  return shot => {
    // Rotate by a small random angle using an integer rotation table.
    const k = r.between(-degrees, degrees);
    const c = 1000 - Math.trunc((k * k * 152) / 1000), s = Math.trunc(k * 17452 / 1000);
    const dx = Math.trunc((shot.dx * c - shot.dy * s) / 1000), dy = Math.trunc((shot.dx * s + shot.dy * c) / 1000);
    const power = Math.max(80, Math.min(1000, shot.power + Math.trunc((shot.power * r.between(-powerPct, powerPct)) / 100)));
    return { dx, dy, power };
  };
}
